import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardDocumentListIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { getApiBaseUrl } from '../services/api';

const API_BASE_URL = getApiBaseUrl();

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

const EVENT_TYPE_COLORS = {
  document_classified: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
  tearline_generated: { bg: 'rgba(16,185,129,0.15)', text: '#34d399' },
  scg_rule_modified: { bg: 'rgba(245,158,11,0.15)', text: '#fbbf24' },
  scg_rule_created: { bg: 'rgba(245,158,11,0.15)', text: '#fbbf24' },
  scg_rule_deleted: { bg: 'rgba(239,68,68,0.15)', text: '#f87171' },
  document_uploaded: { bg: 'rgba(139,92,246,0.15)', text: '#a78bfa' },
  foia_document_processed: { bg: 'rgba(236,72,153,0.15)', text: '#f472b6' },
  foia_redaction_applied: { bg: 'rgba(236,72,153,0.15)', text: '#f472b6' },
  foia_request_submitted: { bg: 'rgba(236,72,153,0.15)', text: '#f472b6' },
  foia_exemption_applied: { bg: 'rgba(236,72,153,0.15)', text: '#f472b6' },
  user_login: { bg: 'rgba(99,102,241,0.15)', text: '#818cf8' },
  user_logout: { bg: 'rgba(99,102,241,0.15)', text: '#818cf8' },
  access_denied: { bg: 'rgba(239,68,68,0.15)', text: '#f87171' },
  document_sanitized: { bg: 'rgba(16,185,129,0.15)', text: '#34d399' },
  classification_changed: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
  classification_downgraded: { bg: 'rgba(16,185,129,0.15)', text: '#34d399' },
  system_config_changed: { bg: 'rgba(245,158,11,0.15)', text: '#fbbf24' },
  bulk_operation_started: { bg: 'rgba(139,92,246,0.15)', text: '#a78bfa' },
  bulk_operation_completed: { bg: 'rgba(139,92,246,0.15)', text: '#a78bfa' },
  sensitive_data_accessed: { bg: 'rgba(239,68,68,0.15)', text: '#f87171' },
  document_downloaded: { bg: 'rgba(139,92,246,0.15)', text: '#a78bfa' },
  search_performed: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
  scg_search_performed: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
};

const LEVEL_STYLES = {
  info: { bg: 'rgba(59,130,246,0.12)', text: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
  warning: { bg: 'rgba(245,158,11,0.12)', text: '#fbbf24', border: 'rgba(245,158,11,0.3)' },
  critical: { bg: 'rgba(239,68,68,0.12)', text: '#f87171', border: 'rgba(239,68,68,0.3)' },
};

export default function AuditLogViewer() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  // Filters
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/audit/stats`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch audit stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', pageSize.toString());
      if (eventTypeFilter) params.set('event_type', eventTypeFilter);
      if (levelFilter) params.set('level', levelFilter);
      if (searchText) params.set('search', searchText);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);

      const resp = await fetch(`${API_BASE_URL}/audit/logs?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError(err.message);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, eventTypeFilter, levelFilter, searchText, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/audit/export`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        alert(errData.detail || 'Export failed');
        return;
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'declass_audit.jsonl';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export audit logs');
    }
  };

  const handleFilterApply = () => {
    setPage(1);
    // fetchLogs will be triggered by useEffect dependency on page change
  };

  const handleClearFilters = () => {
    setEventTypeFilter('');
    setLevelFilter('');
    setSearchText('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return ts;
    }
  };

  const successCount = stats ? (stats.events_by_result?.SUCCESS || 0) : 0;
  const totalEvents = stats ? stats.total_events : 0;
  const successRate = totalEvents > 0 ? ((successCount / totalEvents) * 100).toFixed(1) : '0.0';
  const criticalCount = stats ? (stats.events_by_level?.critical || 0) : 0;
  const recent24h = stats ? stats.recent_24h_count : 0;

  // Gather unique event types from stats for filter dropdown
  const eventTypes = stats ? Object.keys(stats.events_by_type || {}).sort() : [];

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
              Audit Log Viewer
            </h1>
            <div className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[var(--spotlight-glow)] text-[var(--spotlight-gold)] border border-[var(--spotlight-gold)]">
              DECLASS
            </div>
          </div>
          <p className="text-sm text-[var(--text-muted)]">
            Immutable audit trail for all security-sensitive operations
          </p>
        </div>
        <button
          onClick={handleExport}
          className="vault-btn vault-btn-secondary flex items-center gap-2"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Export JSONL
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="vault-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.3)]">
              <ClipboardDocumentListIcon className="w-5 h-5 text-[#60a5fa]" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">Total Events</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                {statsLoading ? '...' : totalEvents.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="vault-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgba(16,185,129,0.12)] border border-[rgba(16,185,129,0.3)]">
              <CheckCircleIcon className="w-5 h-5 text-[#34d399]" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">Success Rate</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                {statsLoading ? '...' : `${successRate}%`}
              </p>
            </div>
          </div>
        </div>

        <div className="vault-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgba(245,158,11,0.12)] border border-[rgba(245,158,11,0.3)]">
              <ShieldCheckIcon className="w-5 h-5 text-[#fbbf24]" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">Events (24h)</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                {statsLoading ? '...' : recent24h.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="vault-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgba(239,68,68,0.12)] border border-[rgba(239,68,68,0.3)]">
              <ExclamationTriangleIcon className="w-5 h-5 text-[#f87171]" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">Critical Events</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                {statsLoading ? '...' : criticalCount.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="vault-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <FunnelIcon className="w-4 h-4 text-[var(--text-muted)]" />
          <span className="text-sm font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Event Type</label>
            <select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              className="w-full rounded border border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] text-sm px-2 py-1.5 focus:outline-none focus:border-[var(--spotlight-gold)]"
            >
              <option value="">All Types</option>
              {eventTypes.map((et) => (
                <option key={et} value={et}>{et.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Level</label>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="w-full rounded border border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] text-sm px-2 py-1.5 focus:outline-none focus:border-[var(--spotlight-gold)]"
            >
              <option value="">All Levels</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded border border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] text-sm px-2 py-1.5 focus:outline-none focus:border-[var(--spotlight-gold)]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded border border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] text-sm px-2 py-1.5 focus:outline-none focus:border-[var(--spotlight-gold)]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-muted)]" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleFilterApply(); }}
                placeholder="Search all fields..."
                className="w-full rounded border border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] text-sm pl-7 pr-2 py-1.5 focus:outline-none focus:border-[var(--spotlight-gold)]"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleFilterApply}
            className="vault-btn vault-btn-primary text-xs px-3 py-1.5"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="vault-btn vault-btn-ghost text-xs px-3 py-1.5"
          >
            Clear All
          </button>
          <span className="text-xs text-[var(--text-muted)] ml-auto">
            {total.toLocaleString()} results
          </span>
        </div>
      </div>

      {/* Logs Table */}
      <div className="vault-card overflow-hidden">
        {error && (
          <div className="p-4 bg-[rgba(239,68,68,0.1)] border-b border-[rgba(239,68,68,0.3)]">
            <p className="text-sm text-[#f87171]">Failed to load audit logs: {error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[var(--spotlight-gold)] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[var(--text-muted)]">Loading audit logs...</span>
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <ClipboardDocumentListIcon className="w-12 h-12 text-[var(--text-muted)] mb-3" />
            <p className="text-[var(--text-secondary)] font-medium">No audit log entries found</p>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              {eventTypeFilter || levelFilter || searchText || startDate || endDate
                ? 'Try adjusting your filters'
                : 'Audit events will appear here as operations are performed'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--vault-border)]">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Timestamp</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Event Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Level</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Resource</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Result</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Classification</th>
                  <th className="w-8 px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((entry, idx) => {
                  const isExpanded = expandedRow === idx;
                  const etColor = EVENT_TYPE_COLORS[entry.event_type] || { bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' };
                  const lvStyle = LEVEL_STYLES[entry.level] || LEVEL_STYLES.info;
                  const isSuccess = entry.result === 'SUCCESS';

                  return (
                    <React.Fragment key={entry.event_id || idx}>
                      <tr
                        className="border-b border-[var(--vault-border)] hover:bg-[var(--vault-elevated)] cursor-pointer transition-colors"
                        onClick={() => setExpandedRow(isExpanded ? null : idx)}
                      >
                        <td className="px-4 py-2.5 text-xs text-[var(--text-secondary)] whitespace-nowrap font-mono">
                          {formatTimestamp(entry.timestamp)}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                            style={{ backgroundColor: etColor.bg, color: etColor.text }}
                          >
                            {(entry.event_type || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                            style={{ backgroundColor: lvStyle.bg, color: lvStyle.text, border: `1px solid ${lvStyle.border}` }}
                          >
                            {entry.level || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[var(--text-secondary)] whitespace-nowrap">
                          {entry.user_id || '-'}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[var(--text-primary)] whitespace-nowrap">
                          {entry.action || '-'}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[var(--text-muted)] whitespace-nowrap font-mono max-w-[140px] truncate" title={entry.resource_id || ''}>
                          {entry.resource_id ? entry.resource_id.substring(0, 16) + (entry.resource_id.length > 16 ? '...' : '') : '-'}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="flex items-center gap-1 text-xs font-medium whitespace-nowrap">
                            {isSuccess
                              ? <CheckCircleIcon className="w-3.5 h-3.5 text-[#34d399]" />
                              : <XCircleIcon className="w-3.5 h-3.5 text-[#f87171]" />
                            }
                            <span style={{ color: isSuccess ? '#34d399' : '#f87171' }}>
                              {entry.result || '-'}
                            </span>
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[var(--text-secondary)] whitespace-nowrap">
                          {entry.classification_level || '-'}
                        </td>
                        <td className="px-2 py-2.5">
                          {isExpanded
                            ? <ChevronUpIcon className="w-4 h-4 text-[var(--text-muted)]" />
                            : <ChevronDownIcon className="w-4 h-4 text-[var(--text-muted)]" />
                          }
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-[var(--vault-border)]">
                          <td colSpan={9} className="px-4 py-4 bg-[var(--vault-bg)]">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--spotlight-gold)]">Event Details</span>
                                {entry.event_id && (
                                  <span className="text-[10px] font-mono text-[var(--text-muted)]">ID: {entry.event_id}</span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                                {entry.domain && (
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Domain</span>
                                    <p className="text-xs text-[var(--text-primary)]">{entry.domain}</p>
                                  </div>
                                )}
                                {entry.resource_type && (
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Resource Type</span>
                                    <p className="text-xs text-[var(--text-primary)]">{entry.resource_type}</p>
                                  </div>
                                )}
                                {entry.confidence_score != null && (
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Confidence</span>
                                    <p className="text-xs text-[var(--text-primary)]">{(entry.confidence_score * 100).toFixed(1)}%</p>
                                  </div>
                                )}
                                {entry.processing_time_ms != null && (
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Processing Time</span>
                                    <p className="text-xs text-[var(--text-primary)]">{entry.processing_time_ms}ms</p>
                                  </div>
                                )}
                                {entry.session_id && (
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Session ID</span>
                                    <p className="text-xs text-[var(--text-primary)] font-mono">{entry.session_id.substring(0, 16)}...</p>
                                  </div>
                                )}
                                {entry.source_ip && (
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Source IP</span>
                                    <p className="text-xs text-[var(--text-primary)] font-mono">{entry.source_ip}</p>
                                  </div>
                                )}
                                {entry.previous_classification && (
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Previous Classification</span>
                                    <p className="text-xs text-[var(--text-primary)]">{entry.previous_classification}</p>
                                  </div>
                                )}
                                {entry.error_message && (
                                  <div className="col-span-2">
                                    <span className="text-[10px] uppercase tracking-wider text-[#f87171]">Error</span>
                                    <p className="text-xs text-[#f87171]">{entry.error_message}</p>
                                  </div>
                                )}
                              </div>
                              {entry.details && Object.keys(entry.details).length > 0 && (
                                <div>
                                  <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] block mb-1">Details (JSON)</span>
                                  <pre className="text-[11px] text-[var(--text-secondary)] bg-[var(--vault-card)] border border-[var(--vault-border)] rounded p-3 overflow-x-auto max-h-48 font-mono leading-relaxed">
                                    {JSON.stringify(entry.details, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--vault-border)]">
            <span className="text-xs text-[var(--text-muted)]">
              Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="vault-btn vault-btn-ghost p-1.5 disabled:opacity-30"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <span className="text-xs text-[var(--text-secondary)] px-3">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="vault-btn vault-btn-ghost p-1.5 disabled:opacity-30"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
